import React, { useState, useRef, useEffect } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Menu from '@shared/shell/Navigation/Menu';
import { useAuth } from '@shared/hooks/useAuth';
import LoadingIndicator from '@shared/ui/common/LoadingIndicator';
import GenericDrawer from '@shared/shell/drawer/GenericDrawer';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import { BottomSheet } from '@shared/shell/bottomsheet/BottomSheet';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';
import { useSubscriptionStatusPopup } from '@shared/hooks/useSubscriptionStatusPopup';
import { UpdateSubscriptionDialog } from './subscription';
import { OrderPlacedDialog } from './purchasing/components/create/OrderPlacedDialog';
import { useOrderPlacedDialogStore } from './purchasing/orderPlacedDialogStore';
import ImpersonationBanner from './company/components/ImpersonationBanner';


const MainScreen: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const mainRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  // Generic drawer
  const { isDrawerOpen, drawerTitle, drawerComponent, closeDrawer, onBack, drawerActions } = useGenericDrawer();

  // Bottom sheet
  const { isBottomSheetOpen, bottomSheetComponent, closeBottomSheet, bottomSheetSize, closeOnBackdropClick } = useBottomSheet();

  // Subscription status popup
  const { isOpen: isSubscriptionPopupOpen, closePopup: closeSubscriptionPopup } = useSubscriptionStatusPopup();

  const orderPlacedPayload = useOrderPlacedDialogStore((s) => s.payload);
  const closeOrderPlacedDialog = useOrderPlacedDialogStore((s) => s.close);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Don't redirect while checking auth status
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', width: '100%', overflow: 'hidden' }}>
        <Menu open={mobileOpen} onClose={handleDrawerToggle} isMobile={isMobile} />
        <Box component="main" sx={{ flexGrow: 1 }}>
          <LoadingIndicator />
        </Box>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace />;
  }

  return (
    <>
      <ImpersonationBanner />

      <Box sx={{
        display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden',
        bgcolor: theme.palette.background.default,
      }}>



        <Menu open={mobileOpen} onClose={handleDrawerToggle} isMobile={isMobile} />

        <Box
          ref={mainRef}
          component="main"
          sx={{
            overflow: 'auto',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >

          <Outlet />
        </Box>

        {/* Generic Drawer */}
        <GenericDrawer
          open={isDrawerOpen}
          onClose={closeDrawer}
          onBack={onBack || undefined}
          title={drawerTitle}
          actions={drawerActions}
        >
          {drawerComponent}
        </GenericDrawer>

        <BottomSheet
          open={isBottomSheetOpen}
          onClose={closeBottomSheet}
          size={bottomSheetSize}
          closeOnBackdropClick={closeOnBackdropClick}
        >
          {bottomSheetComponent}
        </BottomSheet>

        {/* Subscription Status Popup */}
        <UpdateSubscriptionDialog
          open={isSubscriptionPopupOpen}
        />

        {orderPlacedPayload ? (
          <OrderPlacedDialog
            open
            onClose={closeOrderPlacedDialog}
            purchaseOrderId={orderPlacedPayload.purchaseOrderId}
            poNumber={orderPlacedPayload.poNumber}
            supplierEmail={orderPlacedPayload.supplierEmail}
            supplierName={orderPlacedPayload.supplierName}
            supplierPhone={orderPlacedPayload.supplierPhone}
            supplierWebsite={orderPlacedPayload.supplierWebsite}
            supplierAddressLine={orderPlacedPayload.supplierAddressLine}
          />
        ) : null}
      </Box>
    </>
  );
};

export default MainScreen; 